//import * as Utils from '@contentstack/utils'
import { Config, Region, LivePreview, Stack, type LivePreviewQuery } from 'node_modules/contentstack'
import { isVisible, type VisibilitySettings } from 'components/contentstack/helpers/useVisibilityConstrain'
import configLoader from 'config/helper'
import { logError, logWarning, logInfo } from 'telemetry/server'
import { resolveContentstackLanguageCode } from './resolveContentstackLanguageCode'

/* eslint-disable prefer-destructuring */
const CONTENTSTACK_API_KEY = process.env.CONTENTSTACK_API_KEY
const CONTENTSTACK_DELIVERY_TOKEN = process.env.CONTENTSTACK_DELIVERY_TOKEN
const CONTENTSTACK_ENVIRONMENT = configLoader('contentstackEnvironment.environment')
const CONTENTSTACK_BRANCH = configLoader('contentstackBranch.branch')
const CONTENTSTACK_REGION = configLoader('contentstackRegion.region')
const CONTENTSTACK_LIVE_PREVIEW = process.env.CONTENTSTACK_LIVE_PREVIEW
const CONTENTSTACK_PREVIEW_TOKEN = process.env.CONTENTSTACK_PREVIEW_TOKEN
const CONTENTSTACK_PREVIEW_HOST = process.env.CONTENTSTACK_PREVIEW_HOST
const CONTENTSTACK_APP_HOST = configLoader('contentstackAppHost.url')

const isBasicConfigValid = () =>
  !!CONTENTSTACK_API_KEY &&
  !!CONTENTSTACK_DELIVERY_TOKEN &&
  !!CONTENTSTACK_ENVIRONMENT

const isLpConfigValid = () =>
  !!CONTENTSTACK_LIVE_PREVIEW &&
  !!CONTENTSTACK_PREVIEW_TOKEN &&
  !!CONTENTSTACK_PREVIEW_HOST &&
  !!CONTENTSTACK_APP_HOST

const setLivePreviewConfig = (): LivePreview => {
  if (!isLpConfigValid()) throw new Error('Your LP config is set to true. Please make you have set all required LP config in .env')
  return {
    preview_token: CONTENTSTACK_PREVIEW_TOKEN as string,
    enable: CONTENTSTACK_LIVE_PREVIEW === 'true',
    host: CONTENTSTACK_PREVIEW_HOST as string,
  } as LivePreview
}

// singleton
let stackInstance: Stack | undefined = undefined

const initializeContentStackSdk = (): Stack => {
  if (stackInstance) return stackInstance

  if (!isBasicConfigValid()) throw new Error('Please set you .env file')
  const stackConfig: Config = {
    api_key: CONTENTSTACK_API_KEY as string,
    delivery_token: CONTENTSTACK_DELIVERY_TOKEN as string,
    environment: CONTENTSTACK_ENVIRONMENT as string,
    region: CONTENTSTACK_REGION as Region ?? Region.EU,
    branch: CONTENTSTACK_BRANCH ?? 'main',
    fetchOptions: {
      debug: process.env.CONTENTSTACK_SDK_LOGGING === 'true',
      logHandler: (level, data) => {
        const url = decodeURIComponent(data.url)
        logInfo(`UBID: ${process.env.NEXT_PUBLIC_EXT_BUILD_ID} - [Contentstack SDK] ${level} ${url}`)
      },
    },
  }
  if (CONTENTSTACK_LIVE_PREVIEW === 'true') {
    stackConfig.live_preview = setLivePreviewConfig()
  }

  stackInstance = Stack(stackConfig)
  return stackInstance
}

export const setLivePreviewQueryParams = async (queryParams: LivePreviewQuery) => {
  if (queryParams?.live_preview) {
    initializeContentStackSdk().livePreviewQuery(queryParams)
  }
}

/**
 * Drops inactive and time-window-hidden modular blocks from each entry's `content`.
 * Runs inside `unstable_cache` so `Date.now()` does not opt the route into dynamic rendering.
 */
const filterInactiveContentBlocks = <T extends { content?: ContentstackContent[] }>(entries: T[]): T[] =>
  entries.map((entry) => {
    if (!Array.isArray(entry?.content)) return entry

    return {
      ...entry,
      content: entry.content.filter((element) => {
        const [, blockValue] = Object.entries(element).find(([, v]) => v !== undefined) ?? []
        return blockValue !== undefined
          ? isVisible((blockValue as { visibility_settings?: VisibilitySettings }).visibility_settings)
          : true
      }),
    }
  })

type EntryResult<T = ContentstackPage> = {
  data: T[] | null;
  error: string | null;
}

type EntryBaseParams = {
  contentTypeUid: string;
  referenceFieldPath?: string[];
  jsonRtePath?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryBuilder: (query: any) => any;
  locale: string;
}

/** Austrian storefront queries `de-at`; Contentstack stack must define `de` as fallback for that language. */
export const shouldUseContentstackIncludeFallback = (storefrontLocale: string, contentstackLang: string): boolean =>
  storefrontLocale === 'de-AT' && contentstackLang === 'de-at'

const shouldRetryContentstackWithDeOnError = (storefrontLocale: string, primaryLang: string): boolean =>
  storefrontLocale === 'de-AT' && primaryLang !== 'de'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyContentstackLanguage = (contentQuery: any, contentstackLang: string, storefrontLocale: string): void => {
  contentQuery.language(contentstackLang)
  if (shouldUseContentstackIncludeFallback(storefrontLocale, contentstackLang)) {
    contentQuery.includeFallback()
  }
}

export const getEntry = async ({
  contentTypeUid,
  referenceFieldPath,
  queryBuilder,
  locale = 'de-DE',
}: EntryBaseParams): Promise<EntryResult<ContentstackPage | ContentstackElement>> => {

  const primaryLang = resolveContentstackLanguageCode(locale)

  const executeFind = async (lang: string): Promise<EntryResult<ContentstackPage | ContentstackElement>> => {
    try {
      const contentQuery = initializeContentStackSdk().ContentType(contentTypeUid).Query()

      applyContentstackLanguage(contentQuery, lang, locale)

      if (referenceFieldPath) contentQuery.includeReference(referenceFieldPath)

      contentQuery.includeReference('desktop_file')
      contentQuery.addParam('include_dimension', 'true')
      const query = queryBuilder(contentQuery.toJSON())
      const result = await query.find()
      return { data: filterInactiveContentBlocks(result[0] as (ContentstackPage | ContentstackElement)[]), error: null }
    } catch (error) {
      logError(error, { message: 'Error fetching entry', meta: { component: 'getEntry', contentTypeUid } })
      return { data: null, error: 'Failed to fetch data' }
    }
  }

  let result = await executeFind(primaryLang)

  if (shouldRetryContentstackWithDeOnError(locale, primaryLang) && result.error !== null) {
    logInfo('Contentstack AT→DE error retry used', {
      meta: { component: 'getEntry', contentTypeUid, locale: 'de-AT' },
    })
    result = await executeFind('de')
  }

  return result
}

export const getEntryBySlug = async ({
  slug,
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  locale,
}: { slug: string[] } & Omit<EntryBaseParams, 'queryBuilder'>) => getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  queryBuilder: (query) => query.where('url', `/${slug.join('/')}`),
  locale,
})

export const getEntryBySeoSlug = async ({
  slug,
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  locale,
}: { slug: string[] } & Omit<EntryBaseParams, 'queryBuilder'>) => getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  queryBuilder: (query) => query.where('seo_url', `/${slug.join('/')}`),
  locale,
})

export const getEntryByTitle = async ({
  title,
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  locale,
}: { title: string } & Omit<EntryBaseParams, 'queryBuilder'>) => getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  queryBuilder: (query) => query.where('title', title),
  locale,
})

export const getEntryByProductKey = async ({
  productKey,
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  locale,
}: { productKey: string } & Omit<EntryBaseParams, 'queryBuilder'>) => getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  queryBuilder: (query) => query.where('productid.data.key', productKey),
  locale,
})

export const getEntryByCategoryId = async ({
  categoryId,
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  locale,
}: { categoryId: string } & Omit<EntryBaseParams, 'queryBuilder'>) => getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  queryBuilder: (query) => query.where('categoryid.data.id', categoryId).where('seo_url', ''),
  locale,
})

export const getEntryByCategoryKey = async ({
  categoryKey,
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  locale,
}: { categoryKey: string } & Omit<EntryBaseParams, 'queryBuilder'>) => getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  // ONLY FETCH PAGES WITHOUT SEO URL ;)
  queryBuilder: (query) => query.where('categoryid.data.key', categoryKey).where('seo_url', ''),
  locale,
})

export const getEntryByContentTypeUid = async ({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  locale,
}: Omit<EntryBaseParams, 'queryBuilder'>) => getEntry({
  contentTypeUid,
  referenceFieldPath,
  jsonRtePath,
  queryBuilder: (query) => query,
  locale,
})

/**
 * Optional filters for {@link getAllEntriesByType}: non-empty `seo_url` and `seo.enable_search_indexing === true`.
 *
 * Contentstack Delivery returns a limited page size per request; use `limit: 100` (or lower) with `fetchAll: true` to paginate safely.
 * @see https://www.contentstack.com/docs/developers/sdks/content-delivery-sdk/nodejs/get-started-with-nodejs-delivery-sdk
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const applySeoUrlAndSearchIndexingFilters = (query: any) => {
  query.exists('seo_url')
  query.notEqualTo('seo_url', '')
  // Delivery API can still return blank-ish values for some locales; require a non-root path.
  query.regex('seo_url', '^/.+')
  query.where('seo.enable_search_indexing', true)
}

/**
 * Optional filters for {@link getAllEntriesByType} on `brand`: non-empty `url` (non-root path) and
 * `seo.enable_search_indexing === true`. Same pagination guidance as {@link applySeoUrlAndSearchIndexingFilters}.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const applyBrandUrlAndSearchIndexingFilters = (query: any) => {
  query.exists('url')
  query.notEqualTo('url', '')
  query.regex('url', '^/.+')
  query.where('seo.enable_search_indexing', true)
}

export const getAllEntriesByType = async ({
  contentTypeUid,
  referenceFieldPath,
  locale = 'de-DE',
  limit = 250,
  skip = 0,
  fetchAll = false,
  excludeFields = [],
  queryBuilder,
}: {
  contentTypeUid: string
  referenceFieldPath?: string[]
  jsonRtePath?: string[]
  locale?: string
  limit?: number
  skip?: number
  fetchAll?: boolean
  excludeFields?: string[]
  /** Applied after `language` / `includeReference`, before pagination (`includeCount`, `limit`, `skip`, `ascending`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryBuilder?: (query: any) => void
}): Promise<EntryResult<unknown[]> & { count?: number }> => {
  const primaryLang = resolveContentstackLanguageCode(locale)

  const fetchBatch = async (
    batchSkip: number,
    batchLimit: number,
    lang: string,
  ): Promise<{ entries: unknown[]; count: number }> => {
    const contentQuery = initializeContentStackSdk().ContentType(contentTypeUid).Query()

    applyContentstackLanguage(contentQuery, lang, locale)

    if (referenceFieldPath) {
      contentQuery.includeReference(referenceFieldPath)
    }

    queryBuilder?.(contentQuery)

    contentQuery.includeCount()
    contentQuery.limit(batchLimit)
    contentQuery.skip(batchSkip)
    contentQuery.ascending('uid')

    excludeFields.forEach((field: string) => {
      contentQuery.except(field)
    })

    const query = contentQuery.toJSON()
    const result = await query.find()

    return {
      entries: result[0] || [],
      count: result[1] || 0,
    }
  }

  try {
    let activeLang = primaryLang
    const initialSkip = !fetchAll ? skip : 0
    let firstBatch: { entries: unknown[]; count: number }

    try {
      firstBatch = await fetchBatch(initialSkip, limit, activeLang)
    } catch (firstError) {
      if (shouldRetryContentstackWithDeOnError(locale, primaryLang)) {
        logInfo('Contentstack AT→DE error retry used', {
          meta: { component: 'getAllEntriesByType', contentTypeUid, locale: 'de-AT' },
        })
        activeLang = 'de'
        firstBatch = await fetchBatch(initialSkip, limit, activeLang)
      } else {
        throw firstError
      }
    }

    const totalCount = firstBatch.count
    const totalBatches = Math.ceil(totalCount / limit)
    const remainingBatches = totalBatches - 1

    // Sequential fetching to prevent duplicate entries
    const allEntries = await Array.from({ length: remainingBatches }).reduce(
      async (accumulatorPromise: Promise<unknown[]>, _, index: number) => {
        const accumulated = await accumulatorPromise
        const offset = (index + 1) * limit
        const batch = await fetchBatch(offset, limit, activeLang)
        return [...accumulated, ...batch.entries]
      },
      Promise.resolve(firstBatch.entries)
    )

    if (allEntries.length !== totalCount) {
      logWarning(`[Contentstack] Mismatch: Expected ${totalCount} entries but got ${allEntries.length}`, { meta: { component: 'getAllEntriesByType', contentTypeUid } })
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return { data: allEntries as any, error: null, count: totalCount }
  } catch (error) {
    logError(error, { message: 'Error fetching entries', meta: { component: 'getAllEntriesByType', contentTypeUid } })
    return { data: null, error: 'Failed to fetch data' }
  }
}
